import type { CanonicalGame, GameEnergy, GameMechanic } from "@hanjan-shieo/content";

export * from "./commerce";
export * from "./room";
export * from "./meetup";
export * from "./bouncing";
export * from "./gathering";
export * from "./arcade";

export interface Conditions {
  partySize: number;
  selectedProps: string[];
  allowProps?: boolean;
  allowedPlayability: Array<"playable_safe" | "adapted_safe">;
  selectedMechanics?: GameMechanic[];
  selectedEnergy?: GameEnergy[];
  discoveryMode?: "quick" | "explore";
}

export type RoundVerdict = "success" | "close" | "complete" | "pass";
export interface RoundResult {
  verdict: RoundVerdict;
  outcome: SafeOutcome;
  scoreDelta: 0 | 1;
  evidenceLabel: string;
}

export interface SessionState {
  phase: "ready" | "playing" | "reveal" | "rest" | "ended";
  queue: string[];
  currentGameId: string | null;
  roundIndex: number;
  gamesSinceRest: number;
  restInterval: 2 | 3;
  restDeferred: boolean;
  completedGameIds: string[];
  score: number;
  lastOutcome: SafeOutcome | null;
  lastResult: RoundResult | null;
}

export type SafeOutcome = "team_point" | "conversation" | "rest" | "pass";
export type SessionEvent =
  | { type: "START_GAME"; canonicalId: string }
  | { type: "COMPLETE_GAME"; result: RoundResult }
  | { type: "PASS_GAME" }
  | { type: "SHOW_REST" }
  | { type: "DEFER_REST" }
  | { type: "COMPLETE_REST" }
  | { type: "NEXT_READY" }
  | { type: "END_SESSION" };

function bytesToBigInt(bytes: Uint8Array, offset: number): bigint {
  let value = 0n;
  for (let index = offset; index < offset + 8; index += 1) value = (value << 8n) | BigInt(bytes[index] ?? 0);
  return value || 1n;
}

export async function seed128(input: string): Promise<readonly [bigint, bigint]> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(input)));
  return [bytesToBigInt(digest, 0), bytesToBigInt(digest, 8)];
}

export function nextRandom(state: readonly [bigint, bigint]): { value: number; state: readonly [bigint, bigint] } {
  const mask = (1n << 64n) - 1n;
  let [s0, s1] = state;
  const result = (s0 + s1) & mask;
  s1 ^= s0;
  const rotate = (value: bigint, shift: bigint) => ((value << shift) | (value >> (64n - shift))) & mask;
  s0 = (rotate(s0, 55n) ^ s1 ^ ((s1 << 14n) & mask)) & mask;
  s1 = rotate(s1, 36n);
  return { value: Number(result >> 11n) / 9_007_199_254_740_992, state: [s0, s1] };
}

async function tieBreak(seed: string, canonicalId: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(`${seed}|${canonicalId}`));
  return [...new Uint8Array(digest)].slice(0, 8).map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function eligibleGames(games: CanonicalGame[], conditions: Conditions): CanonicalGame[] {
  return games.filter((game) =>
    game.playersMin <= conditions.partySize &&
    (conditions.allowProps || game.props.every((prop) => conditions.selectedProps.includes(prop))) &&
    conditions.allowedPlayability.includes(game.playability as "playable_safe" | "adapted_safe") &&
    (!conditions.selectedMechanics?.length || conditions.selectedMechanics.includes(game.gameplay.mechanic)) &&
    (!conditions.selectedEnergy?.length || conditions.selectedEnergy.includes(game.gameplay.energy)),
  );
}

export async function pickRandomGame(games: CanonicalGame[], conditions: Conditions, recentIds: string[], seed: string): Promise<CanonicalGame | null> {
  const eligible = eligibleGames(games, conditions);
  const recent = new Set(recentIds.slice(-20));
  const fresh = eligible.filter((game) => !recent.has(game.canonicalId));
  const pool = fresh.length ? fresh : eligible;
  if (!pool.length) return null;
  const state = await seed128(`random-picker|${seed}`);
  const index = Math.floor(nextRandom(state).value * pool.length);
  return pool[index] ?? pool.at(-1) ?? null;
}

export async function recommendGames(games: CanonicalGame[], conditions: Conditions, recentIds: string[], seed: string, count = 3): Promise<CanonicalGame[]> {
  const recent = new Map(recentIds.slice(-20).map((id, index, values) => [id, values.length - index]));
  const eligible = eligibleGames(games, conditions);
  const scored = await Promise.all(eligible.map(async (game) => ({
    game,
    score: 100
      + (game.props.length === 0 ? 24 : 0)
      + (game.playability === "playable_safe" ? 12 : 0)
      + (conditions.discoveryMode === "explore" && game.gameplay.novelty === "hidden_find" ? 36 : 0)
      + (conditions.discoveryMode === "explore" && game.gameplay.sourceRegions.length === 1 ? 8 : 0)
      - (recent.has(game.canonicalId) ? 48 : 0),
    tie: await tieBreak(seed, game.canonicalId),
    recentAge: recent.get(game.canonicalId) ?? 0,
  })));
  scored.sort((a, b) => b.score - a.score || a.tie.localeCompare(b.tie) || a.game.canonicalId.localeCompare(b.game.canonicalId));
  const fresh = scored.filter((item) => !recent.has(item.game.canonicalId));
  const selected: typeof scored = [];
  const usedMechanics = new Set<GameMechanic>();
  for (const item of fresh) {
    if (selected.length >= count) break;
    if (usedMechanics.has(item.game.gameplay.mechanic)) continue;
    selected.push(item);
    usedMechanics.add(item.game.gameplay.mechanic);
  }
  if (selected.length < count) selected.push(...fresh.filter((item) => !selected.includes(item)).slice(0, count - selected.length));
  if (selected.length < count) {
    const needed = count - selected.length;
    selected.push(...scored.filter((item) => recent.has(item.game.canonicalId)).sort((a, b) => b.recentAge - a.recentAge || b.score - a.score).slice(0, needed));
  }
  return selected.map((item) => item.game);
}

export function resolveRoundResult(verdict: RoundVerdict, evidenceLabel: string): RoundResult {
  if (verdict === "success") return { verdict, outcome: "team_point", scoreDelta: 1, evidenceLabel };
  if (verdict === "complete") return { verdict, outcome: "team_point", scoreDelta: 1, evidenceLabel };
  if (verdict === "close") return { verdict, outcome: "conversation", scoreDelta: 0, evidenceLabel };
  return { verdict, outcome: "pass", scoreDelta: 0, evidenceLabel };
}

export function explainRecommendation(game: CanonicalGame, mode: "quick" | "explore"): string {
  if (mode === "explore") {
    if (game.gameplay.novelty === "hidden_find") return `원장에서 드물게 발견된 ${game.gameplay.fun}`;
    if (game.gameplay.sourceRegions.includes("global_english") && !game.gameplay.sourceRegions.includes("korea")) return `글로벌 영문권 출처에서 찾은 ${game.gameplay.fun}`;
    return `서로 다른 출처에 등장한 ${game.gameplay.fun}`;
  }
  if (game.props.length === 0) return `준비물 없이 바로 하는 ${game.gameplay.fun}`;
  return `${game.gameplay.durationSec}초 안팎으로 즐기는 ${game.gameplay.fun}`;
}

export async function createSession(queue: string[], roomId: string): Promise<SessionState> {
  const seeded = await seed128(`2026.08.08.1|${roomId}|0|rest`);
  return {
    phase: "ready", queue, currentGameId: null, roundIndex: 0, gamesSinceRest: 0,
    restInterval: nextRandom(seeded).value < 0.5 ? 2 : 3, restDeferred: false,
    completedGameIds: [], score: 0, lastOutcome: null, lastResult: null,
  };
}

export function sessionReducer(state: SessionState, event: SessionEvent): SessionState {
  switch (event.type) {
    case "START_GAME":
      if (!state.queue.includes(event.canonicalId) && state.queue.length > 0) return state;
      return { ...state, phase: "playing", currentGameId: event.canonicalId, lastOutcome: null, lastResult: null };
    case "COMPLETE_GAME": {
      if (state.phase !== "playing" || !state.currentGameId) return state;
      const gamesSinceRest = state.gamesSinceRest + 1;
      return { ...state, phase: "reveal", roundIndex: state.roundIndex + 1, gamesSinceRest, completedGameIds: [...state.completedGameIds, state.currentGameId], score: state.score + event.result.scoreDelta, lastOutcome: event.result.outcome, lastResult: event.result };
    }
    case "PASS_GAME":
      return state.phase === "playing" ? { ...state, phase: "reveal", roundIndex: state.roundIndex + 1, lastOutcome: "pass", lastResult: resolveRoundResult("pass", "불이익 없이 패스") } : state;
    case "SHOW_REST":
      return state.phase === "reveal" || state.phase === "ready" ? { ...state, phase: "rest" } : state;
    case "DEFER_REST":
      return state.phase === "rest" && !state.restDeferred ? { ...state, phase: "ready", restDeferred: true } : state;
    case "COMPLETE_REST":
      return state.phase === "rest" ? { ...state, phase: "ready", gamesSinceRest: 0, restDeferred: false } : state;
    case "NEXT_READY":
      if (state.phase !== "reveal") return state;
      return { ...state, phase: state.gamesSinceRest >= state.restInterval || state.restDeferred ? "rest" : "ready", currentGameId: null };
    case "END_SESSION":
      return { ...state, phase: "ended" };
  }
}

export * from "./partySession";
